/**
 * Hook to fetch Accordion Rich Text links (embedded entries/assets) separately
 * This avoids query complexity issues by not including links in main page query
 */

import {useQuery} from '@tanstack/react-query';
import {print} from 'graphql';
import gql from 'graphql-tag';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';

const ACCORDION_LINKS_QUERY = gql`
  query AccordionLinks($id: String!, $locale: String, $preview: Boolean = false) {
    accordion(id: $id, locale: $locale, preview: $preview) {
      body {
        links {
          entries {
            block {
              __typename
              sys {
                id
              }
            }
          }
          assets {
            block {
              sys {
                id
              }
              contentType
              description
              fileName
              height
              width
              size
              title
              url
            }
          }
        }
      }
    }
  }
`;

/**
 * Fetch links for an Accordion's body field separately
 * @param {string} id - Accordion sys.id
 * @param {Object} options - Query options
 * @returns {Object} - { links, isLoading, error, hasError }
 */
export const useAccordionLinks = (id, options = {}) => {
  const contentfulService = useService(ContentfulService);

  const {data, isLoading, error, isError} = useQuery({
    queryKey: [
      contentfulService?.key,
      'accordionLinks',
      id,
      contentfulService?.locale
    ],

    queryFn: async () => {
      if (!id || !contentfulService) return null;

      const response = await contentfulService.query(
        print(ACCORDION_LINKS_QUERY),
        {
          id,
          locale: contentfulService.locale,
          // Use preview mode to fetch links even if some entries are unpublished
          // This prevents the entire query from failing due to one unpublished entry
          preview: true,
        }
      );

      return response?.accordion?.body?.links || null;
    },

    enabled: !!id && !!contentfulService && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,

    // Don't retry on errors - just skip embedded entries
    retry: false,
  });

  // Log error but don't break - content will render without embedded entries
  if (isError && error) {
    console.warn(`[useAccordionLinks] Failed to load links for ${id}:`, error.message);
  }

  return {
    links: data,
    isLoading,
    error,
    hasError: isError,
  };
};

export default useAccordionLinks;
